import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_TRIES = 8;

function OneToOneScheduler({ sessionId, hours, onBooked }) {
  const dates = [];
  for (let i = 1; i <= 14; i++) {
    const d = new Date(Date.now() + i * 86400000);
    dates.push({ iso: d.toISOString().slice(0, 10), label: d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" }) });
  }
  const times = ["10:00", "11:00", "13:00", "14:00", "16:00", "17:00", "18:00", "19:00"];
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!date || !time) { toast.error("Please pick a date and time"); return; }
    const label = dates.find((d) => d.iso === date)?.label || date;
    setBusy(true);
    try {
      await api.post("/one-to-one/booking", { session_id: sessionId, slot: `${label} at ${time}` });
      toast.success("Session booked!");
      onBooked();
    } catch (e) { toast.error("Could not book — please try again"); setBusy(false); }
  };
  return (
    <div className="w-full text-left" data-testid="one-to-one-scheduler">
      <div className="mx-auto flex flex-col items-center text-center">
        <CheckCircle2 className="text-emerald-600 dark:text-emerald-400" size={56} />
        <h1 className="mt-6 font-serif text-4xl">Payment received</h1>
        <p className="mt-3 text-muted-foreground">Now pick a time for your {hours}-hour 1:1 session with Jason.</p>
      </div>
      <div className="mt-8 space-y-4 rounded-2xl border border-border bg-card p-6">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Preferred date</label>
          <select value={date} onChange={(e) => setDate(e.target.value)} data-testid="booking-date" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none">
            <option value="">Choose a date…</option>
            {dates.map((d) => <option key={d.iso} value={d.iso}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Preferred time</label>
          <select value={time} onChange={(e) => setTime(e.target.value)} data-testid="booking-time" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none">
            <option value="">Choose a time…</option>
            {times.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <Button onClick={submit} disabled={busy} data-testid="booking-confirm" className="w-full rounded-full bg-primary py-6 text-base text-foreground hover:bg-primary/90">
          {busy ? <Loader2 className="mr-2 animate-spin" size={18} /> : <CalendarDays className="mr-2" size={18} />} Confirm booking
        </Button>
        <p className="text-center text-xs text-muted-foreground">Jason will email you to confirm the final details.</p>
      </div>
    </div>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshPurchases } = useAuth();
  const [state, setState] = useState("checking");
  const [amount, setAmount] = useState(null);
  const [txn, setTxn] = useState({});
  const [booked, setBooked] = useState(false);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setTxn(data);
          setBooked(!!data.booked);
          setState("paid");
          await refreshPurchases();
          return;
        }
      } catch (e) {}
      tries.current += 1;
      if (tries.current >= MAX_TRIES) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const isOneToOne = txn.type === "one_to_one";

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto flex max-w-xl flex-col items-center px-4 py-24 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-primary" size={48} />
            <h1 className="mt-6 font-serif text-3xl">Confirming your payment…</h1>
            <p className="mt-2 text-muted-foreground">One moment while we unlock your course.</p>
          </>
        )}
        {state === "paid" && isOneToOne && !booked && (
          <OneToOneScheduler sessionId={sessionId} hours={txn.hours || 1} onBooked={() => setBooked(true)} />
        )}
        {state === "paid" && isOneToOne && booked && (
          <>
            <CheckCircle2 className="text-emerald-600 dark:text-emerald-400" size={56} />
            <h1 className="mt-6 font-serif text-4xl">Session booked!</h1>
            <p className="mt-3 text-muted-foreground">
              Your 1:1 with Jason is confirmed — we've emailed you the details.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/dashboard" data-testid="success-dashboard"><Button className="rounded-full bg-primary text-foreground hover:bg-primary/90">Go to dashboard <ArrowRight size={16} className="ml-2" /></Button></Link>
              <Link to="/trainer"><Button variant="outline" className="rounded-full border-border">Open the Trainer</Button></Link>
            </div>
          </>
        )}
        {state === "paid" && !isOneToOne && (
          <>
            <CheckCircle2 className="text-emerald-600 dark:text-emerald-400" size={56} />
            <h1 className="mt-6 font-serif text-4xl">You're enrolled! 🎼</h1>
            <p className="mt-3 text-muted-foreground">
              Payment of £{amount ? Number(amount).toFixed(0) : ""} received. Your course is fully unlocked.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/courses" data-testid="success-courses"><Button className="rounded-full bg-primary text-foreground hover:bg-primary/90">Go to my courses <ArrowRight size={16} className="ml-2" /></Button></Link>
              <Link to="/dashboard"><Button variant="outline" className="rounded-full border-border">Dashboard</Button></Link>
            </div>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="text-primary" size={48} />
            <h1 className="mt-6 font-serif text-3xl">Still processing…</h1>
            <p className="mt-2 text-muted-foreground">Your payment is taking a little longer. Refresh this page in a moment, or check your dashboard.</p>
            <Link to="/courses" className="mt-6"><Button className="rounded-full bg-primary text-foreground">My courses</Button></Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="text-red-600 dark:text-red-400" size={48} />
            <h1 className="mt-6 font-serif text-3xl">Something went wrong</h1>
            <p className="mt-2 text-muted-foreground">We couldn't find that payment session.</p>
            <Link to="/pricing" className="mt-6"><Button className="rounded-full bg-primary text-foreground">Back to pricing</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
